import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import { Mail, Loader2, CheckCircle, AlertTriangle, Lock, ChevronDown, ChevronUp, Building2, ChevronRight, Fingerprint, X, ShieldCheck, ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'

import { authApi, isTwoFactorRequired } from '@services/api'
import type { LoginResponse } from '@services/api'
import { useAuthStore, UserCompanyRole, type UserContract } from '@stores/authStore'
import { isPushSupported, isPushPermissionDenied, subscribeToPush } from '@services/pushNotifications'
import { isPasskeySupported, authenticateWithPasskey } from '@services/passkey'
import PasskeySetupDialog from '@/components/ui/PasskeySetupDialog'

const EMAIL_COOKIE = 'immotron_email'
const COOKIE_DAYS = 30
const PASSKEY_PROMPT_SHOWN = 'passkey_prompt_shown'

function isMobileDevice(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
}

function getEmailCookie(): string {
  const match = document.cookie.match(new RegExp('(?:^|; )' + EMAIL_COOKIE + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : ''
}

function setEmailCookie(email: string) {
  const expires = new Date()
  expires.setDate(expires.getDate() + COOKIE_DAYS)
  document.cookie = `${EMAIL_COOKIE}=${encodeURIComponent(email)};expires=${expires.toUTCString()};path=/;SameSite=Strict`
}

function schedulePushSubscription() {
  if (isPushSupported() && !isPushPermissionDenied()) {
    setTimeout(() => subscribeToPush(), 2000)
  }
}

type LoginUser = LoginResponse['user']

interface PendingAuth {
  user: LoginUser
  accessToken: string
  refreshToken: string
  companies: UserCompanyRole[]
  contracts: UserContract[]
}

interface TwoFactorState {
  sessionId: string
  emailHint: string
}

const CODE_LENGTH = 4
const RESEND_COOLDOWN_SECONDS = 60

export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setAuth, setActiveCompanies } = useAuthStore()

  const [sentTo, setSentTo] = useState<string | null>(null)
  const [showPasswordSection, setShowPasswordSection] = useState(false)
  const [pendingAuth, setPendingAuth] = useState<PendingAuth | null>(null)
  const [showPasskeyBanner, setShowPasskeyBanner] = useState(false)
  const [showPasskeySetup, setShowPasskeySetup] = useState(false)

  // STORY-173: 2FA state
  const [twoFactorState, setTwoFactorState] = useState<TwoFactorState | null>(null)
  const [codeDigits, setCodeDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''))
  const [resendCooldown, setResendCooldown] = useState(0)
  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([])

  const supportsPasskey = isPasskeySupported()

  // STORY-173: Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    getValues: getEmailValues,
    formState: { errors: emailErrors },
  } = useForm<{ email: string }>({
    defaultValues: { email: getEmailCookie() },
  })

  const {
    register: registerPassword,
    handleSubmit: handlePasswordFormSubmit,
    formState: { errors: passwordErrors },
  } = useForm<{ password: string }>({
    defaultValues: { password: '' },
  })

  const completeLogin = useCallback((data: LoginResponse, options?: { showPasskeyPrompt?: boolean }) => {
    const { user, access_token, refresh_token, companies = [], contracts: rawContracts = [] } = data
    const contracts = rawContracts.map(c => ({
      type: c.contract_type as UserContract['type'],
      status: c.status as UserContract['status'],
      expires_at: c.expires_at,
      id: c.id,
      contract_number: c.contract_number,
      roles: c.roles ?? undefined,
    }))
    const isInternal = user.role === 'admin' || user.role === 'sales'

    if (!isInternal && companies.length > 1) {
      setPendingAuth({ user, accessToken: access_token, refreshToken: refresh_token, companies, contracts })
      return
    }

    setAuth(user, access_token, refresh_token, companies, contracts)
    toast.success(t('dashboard.welcome', { name: user.first_name || user.email }))

    // Show passkey setup banner on mobile after password login (once)
    if (options?.showPasskeyPrompt && supportsPasskey && isMobileDevice() && !localStorage.getItem(PASSKEY_PROMPT_SHOWN)) {
      localStorage.setItem(PASSKEY_PROMPT_SHOWN, '1')
      setShowPasskeyBanner(true)
      // Navigate after short delay so banner is visible
      setTimeout(() => navigate('/dashboard', { replace: true }), 100)
    } else {
      navigate('/dashboard', { replace: true })
    }

    schedulePushSubscription()
  }, [setAuth, navigate, t, supportsPasskey])

  // Magic link mutation
  const magicLinkMutation = useMutation({
    mutationFn: (email: string) => authApi.requestMagicLink(email),
    onSuccess: (_res, email) => {
      setEmailCookie(email)
      setSentTo(email)
    },
  })

  // Password login mutation
  const passwordMutation = useMutation({
    mutationFn: (vars: { email: string; password: string }) => authApi.login(vars),
    onSuccess: (res, vars) => {
      setEmailCookie(vars.email)
      // STORY-173: Check for 2FA required
      if (isTwoFactorRequired(res.data)) {
        setTwoFactorState({
          sessionId: res.data.session_id,
          emailHint: res.data.email_hint,
        })
        setCodeDigits(Array(CODE_LENGTH).fill(''))
        setResendCooldown(RESEND_COOLDOWN_SECONDS)
        // Focus first input after render
        setTimeout(() => codeInputRefs.current[0]?.focus(), 100)
        return
      }
      completeLogin(res.data, { showPasskeyPrompt: true })
    },
  })

  // STORY-173: 2FA verify mutation
  const verifyMutation = useMutation({
    mutationFn: (vars: { session_id: string; code: string }) => authApi.verify2FA(vars),
    onSuccess: (res) => {
      completeLogin(res.data, { showPasskeyPrompt: true })
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { detail?: string } } }
      const message = axiosError.response?.data?.detail || t('auth.twoFactor.invalidCode')
      toast.error(message)
    },
  })

  // STORY-173: 2FA resend mutation
  const resendMutation = useMutation({
    mutationFn: (sessionId: string) => authApi.resend2FA({ session_id: sessionId }),
    onSuccess: () => {
      toast.success(t('auth.twoFactor.resendSuccess'))
      setResendCooldown(RESEND_COOLDOWN_SECONDS)
    },
  })

  // Passkey authentication mutation
  const passkeyMutation = useMutation({
    mutationFn: () => authenticateWithPasskey(),
    onSuccess: (data) => {
      completeLogin(data)
    },
  })

  const handleMagicLinkSubmit = (data: { email: string }) => {
    magicLinkMutation.mutate(data.email)
  }

  const handlePasswordSubmit = (data: { password: string }) => {
    passwordMutation.mutate({ email: getEmailValues('email'), password: data.password })
  }

  const handleResend = () => {
    const email = sentTo ?? getEmailValues('email')
    magicLinkMutation.mutate(email)
    setSentTo(null)
  }

  const handleOpenPasskeySetup = () => {
    setShowPasskeySetup(true)
  }

  // STORY-173: 2FA code input handlers
  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1)
    const newDigits = [...codeDigits]
    newDigits[index] = digit
    setCodeDigits(newDigits)

    // Auto-advance to next input
    if (digit && index < CODE_LENGTH - 1) {
      codeInputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all digits entered
    if (digit && index === CODE_LENGTH - 1) {
      const code = newDigits.join('')
      if (code.length === CODE_LENGTH && twoFactorState) {
        verifyMutation.mutate({ session_id: twoFactorState.sessionId, code })
      }
    }
  }

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus()
    }
  }

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH)
    if (!pasted) return
    const newDigits = Array(CODE_LENGTH).fill('')
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i]
    }
    setCodeDigits(newDigits)
    // Focus last filled or next empty
    const focusIdx = Math.min(pasted.length, CODE_LENGTH - 1)
    codeInputRefs.current[focusIdx]?.focus()

    // Auto-submit if complete
    if (pasted.length === CODE_LENGTH && twoFactorState) {
      verifyMutation.mutate({ session_id: twoFactorState.sessionId, code: pasted })
    }
  }

  const handleVerifySubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const code = codeDigits.join('')
    if (code.length === CODE_LENGTH && twoFactorState) {
      verifyMutation.mutate({ session_id: twoFactorState.sessionId, code })
    }
  }

  const handleBackToLogin = () => {
    setTwoFactorState(null)
    setCodeDigits(Array(CODE_LENGTH).fill(''))
    verifyMutation.reset()
  }

  const handleCompanySelect = (company: UserCompanyRole) => {
    if (!pendingAuth) return
    setAuth(pendingAuth.user, pendingAuth.accessToken, pendingAuth.refreshToken, pendingAuth.companies, pendingAuth.contracts)
    setActiveCompanies([company])
    toast.success(t('dashboard.welcome', { name: pendingAuth.user.first_name || pendingAuth.user.email }))
    navigate('/dashboard', { replace: true })
    schedulePushSubscription()
  }

  // STORY-173: 2FA code entry view
  if (twoFactorState) {
    const twoFactorError = verifyMutation.isError
      ? (() => {
          const status = (verifyMutation.error as import('axios').AxiosError)?.response?.status
          if (status === 410) return t('auth.twoFactor.expiredCode')
          if (status === 429) return t('auth.twoFactor.maxAttempts')
          return t('auth.twoFactor.wrongCode')
        })()
      : null

    return (
      <div className="animate-in">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-primary-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t('auth.twoFactor.title')}
          </h2>
          <p className="text-gray-600 mt-2">
            {t('auth.twoFactor.subtitle', { email: twoFactorState.emailHint })}
          </p>
        </div>

        {twoFactorError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{twoFactorError}</p>
          </div>
        )}

        <form data-testid="auth-login-form-1" onSubmit={handleVerifySubmit} className="space-y-6">
          <div>
            <label className="label text-center block">
              {t('auth.twoFactor.codeLabel')}
            </label>
            <div className="flex justify-center gap-3 mt-2" onPaste={handleCodePaste}>
              {Array.from({ length: CODE_LENGTH }).map((_, i) => (
                <input
                  data-testid="auth-login-input"
                  key={i}
                  ref={(el) => { codeInputRefs.current[i] = el }}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={codeDigits[i]}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  className={`w-14 h-14 text-center text-2xl font-bold rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${
                    twoFactorError
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300 bg-white focus:border-primary-500'
                  }`}
                  aria-label={t('auth.twoFactor.codeLabel') + ` ${i + 1}`}
                />
              ))}
            </div>
          </div>

          <button
            data-testid="auth-login-submit-btn-1"
            type="submit"
            disabled={verifyMutation.isPending || codeDigits.join('').length < CODE_LENGTH}
            className="btn-primary w-full py-3"
          >
            {verifyMutation.isPending ? (
              <>
                <Loader2 size={20} className="animate-spin mr-2" />
                {t('common.loading')}
              </>
            ) : (
              t('auth.twoFactor.submit')
            )}
          </button>
        </form>

        <div className="mt-6 text-center space-y-3">
          <div>
            {resendCooldown > 0 ? (
              <p className="text-sm text-gray-500">
                {t('auth.twoFactor.resendCooldown', { seconds: resendCooldown })}
              </p>
            ) : (
              <button
                data-testid="auth-login-loading-btn"
                type="button"
                onClick={() => resendMutation.mutate(twoFactorState.sessionId)}
                disabled={resendMutation.isPending}
                className="text-sm text-primary-600 hover:text-primary-700 underline"
              >
                {resendMutation.isPending ? t('common.loading') : t('auth.twoFactor.resend')}
              </button>
            )}
          </div>
          <div>
            <button
              data-testid="auth-login-back-to-login-btn"
              type="button"
              onClick={handleBackToLogin}
              className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
            >
              <ArrowLeft size={14} />
              {t('auth.twoFactor.backToLogin')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Company select view (after password login with multiple companies)
  if (pendingAuth) {
    return (
      <div className="animate-in">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900">
            {t('auth.login.selectCompany')}
          </h2>
          <p className="text-gray-600 mt-2">
            {t('auth.login.selectCompanySubtitle')}
          </p>
        </div>
        <div className="space-y-3">
          {pendingAuth.companies.map((company) => (
            <button
              data-testid="auth-login-company-select-btn"
              key={company.id}
              onClick={() => handleCompanySelect(company)}
              className="w-full flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-primary-500 hover:bg-primary-50 transition-colors text-left"
            >
              <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{company.company_name}</p>
                <p className="text-sm text-gray-500">
                  {company.company_uid}
                  {company.function && ` • ${company.function}`}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          ))}
        </div>
      </div>
    )
  }

  // Confirmation view after magic link sent
  if (sentTo) {
    return (
      <div className="animate-in text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('auth.magicLink.sentTitle')}
        </h2>
        <p className="text-gray-600 mb-2">
          {t('auth.magicLink.sentMessage', { email: sentTo })}
        </p>
        <p className="text-sm text-gray-500 mb-8">
          {t('auth.magicLink.sentHint')}
        </p>
        <button
          data-testid="auth-login-resend-btn"
          type="button"
          onClick={handleResend}
          className="text-sm text-primary-600 hover:text-primary-700 underline"
        >
          {t('auth.magicLink.resend')}
        </button>
        <div className="mt-4">
          <button
            data-testid="auth-login-sent-to-btn"
            type="button"
            onClick={() => setSentTo(null)}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            {t('auth.magicLink.wrongAddress')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="animate-in">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-6 h-6 text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          {t('auth.magicLink.title')}
        </h2>
        <p className="text-gray-600 mt-2">
          {t('auth.magicLink.subtitle')}
        </p>
      </div>

      {/* Magic link error */}
      {magicLinkMutation.isError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">
            {t('auth.magicLink.networkError')}
          </p>
        </div>
      )}

      {/* Magic link form */}
      <form data-testid="auth-login-form-2" onSubmit={handleEmailSubmit(handleMagicLinkSubmit)} className="space-y-6">
        <div>
          <label htmlFor="email" className="label">
            {t('auth.login.email')}
          </label>
          <input
            data-testid="auth-login-email-input"
            id="email"
            type="email"
            autoComplete="email"
            placeholder={t('auth.login.emailPlaceholder')}
            className={emailErrors.email ? 'input-error' : 'input'}
            {...registerEmail('email', {
              required: t('validation.required'),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('validation.email'),
              },
            })}
          />
          {emailErrors.email && (
            <p className="error-text">{emailErrors.email.message}</p>
          )}
        </div>

        <button
          data-testid="auth-login-submit-btn-2"
          type="submit"
          disabled={magicLinkMutation.isPending}
          className="btn-primary w-full py-3"
        >
          {magicLinkMutation.isPending ? (
            <>
              <Loader2 size={20} className="animate-spin mr-2" />
              {t('common.loading')}
            </>
          ) : (
            t('auth.magicLink.submit')
          )}
        </button>
      </form>

      {/* Passkey login button */}
      {supportsPasskey && (
        <div className="mt-4">
          <div className="relative flex items-center justify-center my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <span className="relative bg-white px-3 text-sm text-gray-500">
              {t('auth.passkey.divider')}
            </span>
          </div>
          <button
            type="button"
            data-testid="passkey-login"
            onClick={() => passkeyMutation.mutate()}
            disabled={passkeyMutation.isPending}
            className="btn-outline w-full py-3 flex items-center justify-center gap-2"
            aria-label={t('auth.passkey.login')}
          >
            {passkeyMutation.isPending ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Fingerprint size={20} />
            )}
            <span>{t('auth.passkey.login')}</span>
          </button>
          {passkeyMutation.isError && (
            <p className="mt-2 text-sm text-red-600 text-center">
              {(passkeyMutation.error as Error)?.message === 'PASSKEY_CANCELLED'
                ? t('auth.passkey.cancelled')
                : t('auth.passkey.error')}
            </p>
          )}
        </div>
      )}

      {/* Passkey setup banner (mobile, after password login) */}
      {showPasskeyBanner && (
        <div className="mt-4 p-4 bg-primary-50 border border-primary-200 rounded-lg" data-testid="passkey-banner">
          <div className="flex items-start gap-3">
            <Fingerprint className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-primary-900">
                {t('auth.passkey.setupTitle')}
              </p>
              <p className="text-sm text-primary-700 mt-1">
                {t('auth.passkey.setupMessage')}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  data-testid="auth-login-passkey-setup-btn"
                  type="button"
                  onClick={handleOpenPasskeySetup}
                  className="btn-primary text-sm px-3 py-1.5"
                >
                  {t('auth.passkey.setupAction')}
                </button>
                <button
                  data-testid="auth-login-passkey-banner-btn-1"
                  type="button"
                  onClick={() => setShowPasskeyBanner(false)}
                  className="btn-ghost text-sm px-3 py-1.5"
                >
                  {t('auth.passkey.setupDismiss')}
                </button>
              </div>
            </div>
            <button
              data-testid="auth-login-passkey-banner-btn-2"
              type="button"
              onClick={() => setShowPasskeyBanner(false)}
              className="text-primary-400 hover:text-primary-600"
              aria-label={t('common.close')}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <PasskeySetupDialog
        open={showPasskeySetup}
        onClose={() => {
          setShowPasskeySetup(false)
          setShowPasskeyBanner(false)
        }}
      />

      {/* Password fallback toggle */}
      <div className="mt-6">
        <button
          type="button"
          data-testid="password-toggle"
          onClick={() => setShowPasswordSection((prev) => !prev)}
          className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          aria-expanded={showPasswordSection}
        >
          <Lock size={14} />
          <span>{t('auth.login.withPassword')}</span>
          {showPasswordSection ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {showPasswordSection && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            {/* Password error */}
            {passwordMutation.isError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">
                  {t('auth.login.passwordError')}
                </p>
              </div>
            )}

            <form data-testid="auth-login-form-3" onSubmit={handlePasswordFormSubmit(handlePasswordSubmit)} className="space-y-4">
              <div>
                <label htmlFor="password" className="label">
                  {t('auth.login.password')}
                </label>
                <input
                  data-testid="auth-login-password-input"
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder={t('auth.login.passwordPlaceholder')}
                  className={passwordErrors.password ? 'input-error' : 'input'}
                  {...registerPassword('password', {
                    required: t('validation.required'),
                  })}
                />
                {passwordErrors.password && (
                  <p className="error-text">{passwordErrors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <Link
                  data-testid="auth-login-forgot-password-link"
                  to="/forgot-password"
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {t('auth.login.forgotPassword')}
                </Link>
                <Link
                  data-testid="auth-login-developer-signup-link"
                  to="/register/developer"
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {t('developer.signup.loginLink')}
                </Link>
              </div>

              <button
                data-testid="auth-login-submit-btn-3"
                type="submit"
                disabled={passwordMutation.isPending}
                className="btn-primary w-full py-3"
              >
                {passwordMutation.isPending ? (
                  <>
                    <Loader2 size={20} className="animate-spin mr-2" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('auth.login.submit')
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
